import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  signal,
  input,
  effect,
  computed,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep, isEqual, isNil } from 'lodash';
import { MenuItem } from 'primeng/api';
import {} from '../../utils/window-size-observer';
import { isKey, keyCodes, windowSizeObserver } from '@local/ts-infra';

export interface Tab {
  name?: string;
  icon?: string;
  amount?: number;
  tooltip?: string;
  id: string;
  readonly?: boolean;
  disabled?: boolean;
  editMode?: boolean;
  hideWhenNotAdmin?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'u-tab-view',
  templateUrl: './u-tab-view.component.html',
  styleUrls: ['./u-tab-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UTabViewComponent implements AfterViewInit {
  private readonly TAB_ACTION_WIDTH = 24;
  private readonly TAB_ICON_WIDTH = 24;
  private readonly MORE_DROPDOWN_WIDTH = 70;
  private readonly PADDING_TAB = 8;
  private readonly GAP_TAB = 8;
  private readonly TAB_MAX_WIDTH = 240;
  private readonly TAB_NAME_LETTER_PX_SIZE = 8.5;
  private windowSize$ = windowSizeObserver();

  tabsStaticCurrent = signal<Tab[]>([]);
  tabsMovingCurrent = signal<Tab[]>([]);
  previousIndex = signal<number | undefined>(undefined);
  currentIndex = signal<number | undefined>(undefined);
  progressInitTab = signal<boolean>(false);
  firstInit = signal<boolean>(true);
  readonly INPUT_STYLES = {
    'background-color': 'transparent',
    fontSize: '14px',
    border: 'none',
    borderRadius: '0',
    padding: '0',
  };
  activeTabId = signal<string | undefined>(undefined);
  staticTabsLimit = signal<number>(0);
  movingTabsLimit = signal<number>(0);
  showOnOptions = signal<MenuItem[]>([]);
  currentSelectedTabId = signal<string>('');

  showMoreOptions = computed<boolean>(() => {
    return !this.hideMoreOptions() && this.showOnOptions()[0]?.items?.length > 0;
  });

  tabsStatic = input<Tab[]>([]);
  tabsMoving = input<Tab[]>([]);
  hideMoreOptions = input<boolean>(false);
  selectedTabId = input<string>('');
  tabsActionStatic = input<Tab[]>([]);
  openLeftMenuBar = input<boolean>(false);
  hasDelayTab = input<boolean>(true);
  isPrimary = input<boolean>(true);
  contentMaxWidth = input<number>(0);

  @Output() onChangeActiveTab = new EventEmitter<any>();
  @Output() onAction = new EventEmitter<any>();
  @Output() onOpenContextMenu = new EventEmitter<any>();
  @Output() onItemsOrderChange = new EventEmitter<Tab[]>();

  @ViewChild('tabViewContainer', { read: ElementRef }) tabViewContainerRef: ElementRef;
  @ViewChildren('staticTabs', { read: ElementRef }) staticTabsRef: QueryList<ElementRef>;
  @ViewChildren('movingTabs', { read: ElementRef }) movingTabsRef: QueryList<ElementRef>;
  @ViewChild('tabsContainer', { read: ElementRef }) tabsContainerRef: ElementRef;
  @ViewChild('static', { read: ElementRef }) staticTabsContainerRef: ElementRef;
  @ViewChild('moving', { read: ElementRef }) movingTabsContainerRef: ElementRef;

  @HostBinding('style.--menubar-list-right-position') get rightListPosition() {
    if (!this.openLeftMenuBar()) {
      return;
    }
    let tabsContainerWidth = 0;
    if (this.movingTabsContainerRef) {
      tabsContainerWidth += this.movingTabsContainerRef.nativeElement.offsetWidth;
    }
    if (this.staticTabsContainerRef) {
      tabsContainerWidth += this.staticTabsContainerRef.nativeElement.offsetWidth;
    }
    tabsContainerWidth += this.MORE_DROPDOWN_WIDTH;
    const right = window.innerWidth - tabsContainerWidth;
    return `${right}px`;
  }

  constructor() {
    effect(
      () => {
        const value = this.tabsStatic();
        if (isEqual(this.tabsStaticCurrent(), value)) {
          return;
        }
        this.tabsStaticCurrent.set(cloneDeep(value));

        this.initTabs();
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const value = this.tabsMoving();
        if (isEqual(this.tabsMovingCurrent(), value)) {
          return;
        }

        const editMode = this.tabsMovingCurrent()?.filter((e) => !!e?.editMode) || [];
        if (editMode?.length && editMode.every((t) => !!value.find((e) => t.id === e.id)?.editMode)) {
          return;
        }

        this.tabsMovingCurrent.set(cloneDeep(value));

        this.initTabs();
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const selectedId = this.selectedTabId();
        if (selectedId && selectedId !== this.currentSelectedTabId()) {
          this.currentSelectedTabId.set(selectedId);
          this.activeTabId.set(selectedId);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngAfterViewInit(): void {
    this.initTabs();
    this.windowSize$.pipe(untilDestroyed(this)).subscribe(() => {
      this.initTabs();
    });
  }

  private initTabs() {
    if (this.tabsMoving()?.length) {
      this.staticTabsLimit.set(this.tabsStatic().length);
    }
    if (this.progressInitTab()) return;
    this.progressInitTab.set(true);

    const tabs = [...this.tabsStaticCurrent(), ...this.tabsMovingCurrent()];
    this.activeTabId.set(tabs[0]?.id);
    this.showOnOptions.set([{ label: 'More', items: [] }]);
    setTimeout(
      () => {
        this.addTooltipToTab();
        this.setTabsLimit();
        this.progressInitTab.set(false);
        this.firstInit.set(false);
      },
      this.firstInit() && this.hasDelayTab() ? 700 : 0
    );
  }

  addTooltipToTab() {
    const tabsMovingRef = this.movingTabsRef?.toArray();
    tabsMovingRef?.forEach((tab, index) => {
      if (tab.nativeElement?.offsetWidth === this.TAB_MAX_WIDTH) {
        const tab = this.tabsMovingCurrent()[index];
        this.tabsMovingCurrent.update((tabs) => {
          tabs[index] = { ...tab, tooltip: tab?.name };
          return tabs;
        });
      }
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    this.previousIndex.set(event.previousIndex);
    this.currentIndex.set(event.currentIndex);

    this.tabsMovingCurrent.update((tabs) => {
      const updatedTabs = [...tabs];
      moveItemInArray(updatedTabs, event.previousIndex, event.currentIndex);
      return updatedTabs;
    });

    const tabs = [...this.tabsStaticCurrent(), ...this.tabsMovingCurrent()];
    if (this.previousIndex() !== this.currentIndex()) {
      this.onItemsOrderChange.emit(tabs);
    }
    this.openTab(this.tabsMovingCurrent()[this.currentIndex()]);
  }

  openTab(tab: any) {
    if (!tab || tab?.editMode || tab?.disabled) {
      return;
    }
    this.currentSelectedTabId.set(tab.id);
    this.activeTabId.set(tab.id);
    this.onChangeActiveTab.emit({ tab: tab.id });
  }

  doAction(event, action: string) {
    this.onAction.emit({ event, action });
  }

  private showAndHideTabs(event, tabsLimit: number, tabs: Tab[]) {
    const index = tabs.findIndex((obj) => obj?.id === event?.item?.id);
    if (index < 0) {
      return;
    }
    const selectedItem = tabs[index];
    tabs.splice(index, 1);
    const item = tabs[tabsLimit - 1];
    tabs.splice(tabsLimit - 1, 1, selectedItem);
    tabs.push(item);
    const label =
      item?.amount !== null && item?.amount !== undefined ? `${item?.name} <span class="tab-amount">${item?.amount}</span>` : item?.name;
    this.showOnOptions.update((options) => {
      const updatedOptions = [...options];
      if (updatedOptions[0]) {
        updatedOptions[0].items.unshift({
          label,
          icon: item?.icon,
          id: item?.id,
          escape: false,
          command: (event) => {
            this.showAndHideTabs(event, tabsLimit, tabs);
          },
        });
      }
      return updatedOptions;
    });
    this.showOnOptions.update((options) => {
      const updatedOptions = [...options];
      updatedOptions[0].items = updatedOptions[0].items.filter((obj) => obj?.id !== event?.item?.id);
      return updatedOptions;
    });
    this.openTab(selectedItem);
  }

  renameTab($event, tab: Tab) {
    this.tabsMovingCurrent.update((tabs) => {
      return tabs.map((t) => (t.id === tab.id ? { ...t, editMode: false } : t));
    });

    this.onAction.emit({ $event, action: 'rename', tabId: tab.id });
  }

  openContextMenu(event, tabId: string) {
    event.preventDefault();
    this.onOpenContextMenu.emit({ event, tabId });
  }

  setTabsLimit() {
    this.showOnOptions.update((options) => {
      const updatedOptions = [...options];
      if (updatedOptions[0]) {
        updatedOptions[0].items = [];
      }
      return updatedOptions;
    });
    if (!this.tabsMoving()?.length) {
      this.setStaticTabsLimit();
      return;
    }
    this.setMovingTabsLimit();
  }

  private setStaticTabsLimit() {
    this.tabsStaticCurrent.set(cloneDeep(this.tabsStatic())); //keeps static tabs initial order
    let tabsLimit = 0;
    let width = 0;

    if (!this.staticTabsRef?.toArray()) {
      return;
    }

    width = this.TAB_ACTION_WIDTH * (this.tabsActionStatic()?.length || 0);
    if (!this.hideMoreOptions()) {
      width += this.MORE_DROPDOWN_WIDTH;
    }

    const tabViewWidth = this.tabsContainerRef.nativeElement.offsetWidth;
    this.tabsStaticCurrent()?.forEach((tab) => {
      width += this.PADDING_TAB + this.GAP_TAB;
      if (tab?.name) {
        width += tab?.name?.length * this.TAB_NAME_LETTER_PX_SIZE;
      }
      if (tab?.icon) {
        width += this.TAB_ICON_WIDTH;
      }
      if (tabViewWidth > width) {
        tabsLimit++;
      }
    });

    if (!this.hideMoreOptions()) {
      this.insertTabsToDropdown(tabsLimit, this.tabsStaticCurrent());
      this.tabsStaticCurrent()?.forEach((item, index) => {
        if (this.selectedTabId() === item?.id && index >= tabsLimit) {
          this.showAndHideTabs({ item }, tabsLimit, this.tabsStaticCurrent());
        }
      });
    }

    this.staticTabsLimit.set(tabsLimit);
  }

  private setMovingTabsLimit() {
    let tabsLimit = 0;
    let width = 0;
    let firstTabsWidth = 0;
    this.staticTabsLimit.set(this.tabsStatic()?.length || 0);

    const firstStaticTabs = this.staticTabsRef?.toArray();
    if (!this.movingTabsRef?.toArray() || !firstStaticTabs) {
      return;
    }

    firstStaticTabs?.forEach((tab) => {
      firstTabsWidth += tab.nativeElement.offsetWidth + this.PADDING_TAB;
    });

    width = firstTabsWidth + this.MORE_DROPDOWN_WIDTH + this.GAP_TAB;

    let tabViewWidth = this.tabsContainerRef.nativeElement.offsetWidth;
    if (this.contentMaxWidth()) {
      const tabViewMaxWidth = this.tabViewContainerRef.nativeElement.offsetWidth - this.contentMaxWidth();
      tabViewWidth = Math.min(tabViewWidth, tabViewMaxWidth);
    }

    this.tabsMovingCurrent()?.forEach((tab) => {
      width += tab?.name?.length * this.TAB_NAME_LETTER_PX_SIZE + this.PADDING_TAB + this.GAP_TAB;
      if (tabViewWidth > width) {
        tabsLimit++;
      }
    });

    this.insertTabsToDropdown(tabsLimit, this.tabsMovingCurrent());

    this.tabsMovingCurrent()?.forEach((item, index) => {
      if ((item?.editMode || this.selectedTabId() === item?.id) && index >= tabsLimit) {
        this.showAndHideTabs({ item }, tabsLimit, this.tabsMovingCurrent());
      }
    });
    this.movingTabsLimit.set(tabsLimit);
  }

  private insertTabsToDropdown(tabsLimit: number, tabs: Tab[]) {
    if (tabsLimit >= tabs?.length) {
      return;
    }
    tabs?.slice(tabsLimit).forEach((tab) => {
      if (tab) {
        const label =
          tab.amount !== null && tab.amount !== undefined ? `${tab?.name} <span class="tab-amount">${tab.amount}</span>` : tab.name;
        this.showOnOptions.update((options) => {
          const updatedOptions = [...options];
          if (updatedOptions[0]) {
            updatedOptions[0].items.push({
              label,
              icon: tab.icon,
              id: tab.id,
              escape: false,
              command: (event) => {
                this.showAndHideTabs(event, tabsLimit, tabs);
              },
            });
          }
          return updatedOptions;
        });
      }
    });
    this.tabsActionStatic()?.forEach((tab) => {
      if (tab) {
        this.showOnOptions.update((options) => {
          const updatedOptions = [...options];
          updatedOptions[0].items.push({
            label: tab?.name,
            icon: tab.icon,
            command: (event) => {
              this.doAction(event, tab?.name);
            },
          });
          return updatedOptions;
        });
      }
    });
  }

  checkIsNil(value: any): boolean {
    return isNil(value);
  }

  onKeydown(event: KeyboardEvent, tab: Tab) {
    event.stopPropagation();
    if (isKey(event, keyCodes.escape)) {
      this.renameTab({ event, value: tab.name }, tab);
    }
  }
}
